import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { setData, setIsSaveDraft } from '../../../features/aiBotSlice';
import { findTag, getOrganizationMembers } from '../../../features/mainSlice';
import { checkInsightArkProduct } from '../../../features/organizationSlice';
import Button from '../../shared/Button';
import PageTitle from '../PageTitle';
import RowTitle from '../RowTitle';
import AutoExit from './AutoExit';
import EndDetection from './EndDetection';
import OosMessage from './OosMessage';
import RateLimit from './RateLimit';
import ReplyButton from './ReplyButton';
import Trigger from './Trigger';
import Welcome from './Welcome';

const useStyles = makeStyles(
  {
    root: {},
    card: {
      display: 'flex',
      alignItems: 'start',
    },
  },
  {
    name: 'TriggerSettingContainer',
  },
);

let findTagTimer = null;
let findMemberTimer = null;

const TriggerSettingContainer = ({ pathKey }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { orgId, id } = useParams();
  const { isSaved, data, isActive } = useSelector(store => store.aiBot);
  const { candidateTags, members, isLoading, botList } = useSelector(
    store => store.main,
  );
  const {
    keyword,
    autoTime,
    hitTags,
    welcomeMessage,
    continueMessage,
    oosMessage,
    autoExit,
    rejectReply,
    rateLimit,
    endDetection,
    reflectMode,
    openLivechatBot,
  } = data;

  const memberList = members.map(m => ({
    ...m,
    displayName: `${m?.firstName} ${m?.lastName}`,
  }));

  const handleChange = value => {
    dispatch(setData(value));
    dispatch(setIsSaveDraft(true));
  };

  const handleNext = () => {
    history.push(`/${pathKey}/${orgId}/${id}/advanceSetting`);
  };

  const handleTagChange = async value => {
    clearTimeout(findTagTimer);
    findTagTimer = setTimeout(() => {
      dispatch(findTag(value, orgId, undefined, { limit: 20 }));
    }, 1000);
  };

  const handleMemberChange = async value => {
    clearTimeout(findMemberTimer);
    findMemberTimer = setTimeout(() => {
      dispatch(
        getOrganizationMembers({
          orgId,
          limit: 20,
          keyword: value,
        }),
      );
    }, 1000);
  };

  function handleChangeButton(buttonData) {
    handleChange({
      continueMessage: {
        ...continueMessage,
        buttons: buttonData,
      },
    });
  }

  const handleCheckInsightArkProduct = callback => {
    dispatch(checkInsightArkProduct(callback));
  };

  return (
    <div className={classes.root}>
      <PageTitle title={t(`${pathKey}:triggerSetting`)} />
      <Trigger
        onChange={handleChange}
        hitTags={hitTags}
        keyword={keyword}
        autoTime={autoTime}
        isSaved={isSaved}
        pathKey={pathKey}
        onCheckInsightArkProduct={handleCheckInsightArkProduct}
        openLivechatBot={openLivechatBot}
      />
      <Welcome
        onChange={handleChange}
        welcomeMessage={welcomeMessage}
        isSaved={isSaved}
      />
      <RowTitle
        title={t(`${pathKey}:triggerSettingContainer.endingCardTitle`)}
        tooltip={t(`${pathKey}:triggerSettingContainer.endingCardTooltip`)}
        isRequired
      />
      <ReplyButton
        reflectMode={reflectMode}
        rejectReply={rejectReply}
        buttons={continueMessage?.buttons}
        candidateTags={candidateTags}
        members={memberList}
        botList={botList}
        onChange={handleChange}
        onButtonChange={buttonData => handleChangeButton(buttonData)}
        onTagChange={handleTagChange}
        onMemberChange={handleMemberChange}
        isSaved={isSaved}
        isLoading={isLoading}
        disabled={isActive}
        onCheckInsightArkProduct={handleCheckInsightArkProduct}
        onRejectReplyChange={v => {
          handleChange({
            rejectReply: {
              ...rejectReply,
              ...v,
            },
          });
        }}
      />
      <EndDetection
        onChange={v =>
          handleChange({
            endDetection: {
              ...endDetection,
              message: v,
            },
          })
        }
        value={endDetection?.message}
        isChecked={endDetection?.enabled}
        onSwitchChange={() => {
          handleChange({
            endDetection: {
              ...endDetection,
              enabled: !endDetection?.enabled,
            },
          });
        }}
        isSaved={isSaved}
      />
      <AutoExit
        onMemberChange={handleMemberChange}
        onChange={v =>
          handleChange({
            autoExit: {
              ...autoExit,
              ...v,
            },
          })
        }
        message={autoExit?.message}
        interval={autoExit?.interval}
        isChecked={autoExit?.enabled}
        inbox={autoExit?.inbox}
        userId={autoExit?.userId}
        displayName={autoExit?.displayName}
        members={memberList}
        onCheckInsightArkProduct={handleCheckInsightArkProduct}
        onSwitchChange={() => {
          handleChange({
            autoExit: {
              ...autoExit,
              enabled: !autoExit?.enabled,
            },
          });
        }}
        isSaved={isSaved}
      />
      <OosMessage
        onChange={v =>
          handleChange({
            oosMessage: {
              ...oosMessage,
              value: v,
            },
          })
        }
        value={oosMessage?.value}
        isChecked={oosMessage?.enabled}
        onSwitchChange={() => {
          handleChange({
            oosMessage: {
              ...oosMessage,
              enabled: !oosMessage?.enabled,
            },
          });
        }}
        isSaved={isSaved}
      />
      <RateLimit
        onChange={v =>
          handleChange({
            rateLimit: {
              ...rateLimit,
              ...v,
            },
          })
        }
        onSwitchChange={() => {
          handleChange({
            rateLimit: {
              ...rateLimit,
              enabled: !rateLimit?.enabled,
            },
          });
        }}
        message={rateLimit?.message}
        limit={rateLimit?.limit}
        isChecked={rateLimit?.enabled}
        isSaved={isSaved}
      />
      <Button topic="primaryGreen" onClick={handleNext}>
        {t('next')}
      </Button>
    </div>
  );
};

export default TriggerSettingContainer;
